/**
 * JobEditor — Tabbed editor for the full mapping sheet of a job.
 *
 * Tab bar: Source · Target · Column Mapping · CTEs · Filters · DQ Rules · Strategy · Run · SQL Preview
 *
 * Each editable tab has its own Save button that calls the relevant PATCH
 * endpoint. Unsaved-changes amber dot indicator per editable tab.
 * 409 conflict shows an inline banner (not a toast).
 *
 * Run and SQL Preview tabs are read/action tabs — no dirty indicator.
 *
 * Loads full job detail on mount via getJob().
 */

import { useCallback, useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, Database, Loader2 } from "lucide-react";
import type { PEJob, PEPipeline } from "../../features/pipeline-engineer/types";
import { getJob } from "../../features/pipeline-engineer/api/pe-client";
import { SourceTablesPanel } from "./job-editor/SourceTablesPanel";
import { TargetTablePanel } from "./job-editor/TargetTablePanel";
import { ColumnMappingGrid } from "./job-editor/ColumnMappingGrid";
import { CTEStepPanel } from "./job-editor/CTEStepPanel";
import { FilterPanel } from "./job-editor/FilterPanel";
import { DQRulesPanel } from "./job-editor/DQRulesPanel";
import { StrategyPanel } from "./job-editor/StrategyPanel";
import { RunPanel } from "./job-editor/RunPanel";
import { SQLPreviewPanel } from "./job-editor/SQLPreviewPanel";

// ---------------------------------------------------------------------------
// Tab definition
// ---------------------------------------------------------------------------

type TabKey =
  | "source"
  | "target"
  | "columns"
  | "ctes"
  | "filters"
  | "dq"
  | "strategy"
  | "run"
  | "sql-preview";

interface Tab {
  key: TabKey;
  label: string;
}

const TABS: Tab[] = [
  { key: "source", label: "Source" },
  { key: "target", label: "Target" },
  { key: "columns", label: "Column Mapping" },
  { key: "ctes", label: "CTEs" },
  { key: "filters", label: "Filters" },
  { key: "dq", label: "DQ Rules" },
  { key: "strategy", label: "Strategy" },
  { key: "run", label: "Run" },
  { key: "sql-preview", label: "SQL Preview" },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface JobEditorProps {
  pipelineId: string;
  jobId: string;
  /** Project ID — passed to AI-assisted components (CTEAIPanel, NLToSQLPopover). */
  projectId: string;
  /** Pipeline object — provides source/target connection details for the header info bar. */
  pipeline?: PEPipeline | null;
  onBack: () => void;
  /** Reports whether any tab has unsaved edits so the host can confirm before
   * navigating away by another route (top tabs, catalog). Must be stable. */
  onUnsavedChange?: (hasUnsaved: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JobEditor({
  pipelineId,
  jobId,
  projectId,
  pipeline,
  onBack,
  onUnsavedChange,
}: JobEditorProps) {
  const [job, setJob] = useState<PEJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("source");
  const [dirty, setDirty] = useState<Record<TabKey, boolean>>({
    source: false,
    target: false,
    columns: false,
    ctes: false,
    filters: false,
    dq: false,
    strategy: false,
    run: false,
    "sql-preview": false,
  });
  const [conflictWarning, setConflictWarning] = useState(false);

  // ── Load job detail ────────────────────────────────────────────────────────
  const fetchJob = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getJob(pipelineId, jobId);
      setJob(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load job");
    } finally {
      setLoading(false);
    }
  }, [pipelineId, jobId]);

  useEffect(() => {
    void fetchJob();
  }, [fetchJob]);

  // Silent refresh (no loading spinner, which would unmount the active tab):
  // a schema-drift fix changes the job's mapping from the Run tab.
  const handleJobChanged = useCallback(() => {
    getJob(pipelineId, jobId)
      .then(setJob)
      .catch(() => undefined);
  }, [pipelineId, jobId]);

  // ── Dirty trackers per tab (stable references — passed as onDirtyChange props
  //    to avoid infinite re-render loops from dep array changes) ──────────────
  const handleSourceDirty = useCallback((isDirty: boolean) => {
    setDirty((prev) => ({ ...prev, source: isDirty }));
  }, []);
  const handleTargetDirty = useCallback((isDirty: boolean) => {
    setDirty((prev) => ({ ...prev, target: isDirty }));
  }, []);
  const handleColumnsDirty = useCallback((isDirty: boolean) => {
    setDirty((prev) => ({ ...prev, columns: isDirty }));
  }, []);
  const handleCtesDirty = useCallback((isDirty: boolean) => {
    setDirty((prev) => ({ ...prev, ctes: isDirty }));
  }, []);
  const handleFiltersDirty = useCallback((isDirty: boolean) => {
    setDirty((prev) => ({ ...prev, filters: isDirty }));
  }, []);
  const handleDqDirty = useCallback((isDirty: boolean) => {
    setDirty((prev) => ({ ...prev, dq: isDirty }));
  }, []);
  const handleStrategyDirty = useCallback((isDirty: boolean) => {
    setDirty((prev) => ({ ...prev, strategy: isDirty }));
  }, []);

  const handleTabChange = (nextTab: TabKey) => {
    if (nextTab === activeTab) return;
    if (dirty[activeTab]) {
      const activeLabel =
        TABS.find((tab) => tab.key === activeTab)?.label ?? "this tab";
      if (
        !window.confirm(
          `Discard unsaved changes in ${activeLabel} before switching tabs?`,
        )
      ) {
        return;
      }
      setDirty((prev) => ({ ...prev, [activeTab]: false }));
    }
    setActiveTab(nextTab);
  };

  // ── Leaving the job (Back / reload / closing the tab) ─────────────────────
  // The tab-switch guard above only covers the active tab; every tab keeps its
  // own unsaved edits, so leaving checks all of them.
  const dirtyTabLabels = TABS.filter((tab) => dirty[tab.key]).map(
    (tab) => tab.label,
  );
  const hasUnsavedChanges = dirtyTabLabels.length > 0;

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    onUnsavedChange?.(true);
    return () => onUnsavedChange?.(false);
  }, [hasUnsavedChanges, onUnsavedChange]);

  const handleBack = () => {
    if (
      hasUnsavedChanges &&
      !window.confirm(
        `Discard unsaved changes in ${dirtyTabLabels.join(", ")} and go back to the jobs list?`,
      )
    ) {
      return;
    }
    onBack();
  };

  // ── After a tab saves successfully ────────────────────────────────────────
  const handleSaved = useCallback(
    (updated: PEJob) => {
      setJob(updated);
      setConflictWarning(false);
      // Reset dirty for the active tab only (other tabs stay dirty if edited)
      setDirty((prev) => ({ ...prev, [activeTab]: false }));
    },
    [activeTab],
  );

  const handleConflict = useCallback(() => {
    setConflictWarning(true);
  }, []);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-8 flex items-center justify-center min-h-48">
        <Loader2 size={24} className="animate-spin text-white/30" />
      </div>
    );
  }

  if (loadError !== null || job === null) {
    return (
      <div className="glass-card rounded-2xl p-6 space-y-4">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
        >
          <ChevronLeft size={14} aria-hidden="true" />
          Back to jobs
        </button>
        <p className="text-red-400 text-sm" role="alert">
          {loadError ?? "Job not found."}
        </p>
        <button
          type="button"
          onClick={() => void fetchJob()}
          className="text-xs text-brand-400 hover:text-brand-300"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-6 space-y-5">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
          >
            <ChevronLeft size={14} aria-hidden="true" />
            Jobs
          </button>
          <span className="text-white/20" aria-hidden="true">
            /
          </span>
          <h2 className="text-sm font-semibold text-white">{job.jobName}</h2>
        </div>
        <p className="text-xs text-white/40 pl-[3.25rem]">{job.targetTable}</p>

        {/* ── Connection info bar (source → target) ─────────────────── */}
        {pipeline != null && <ConnectionInfoBar pipeline={pipeline} />}
      </div>

      {/* ── Conflict warning (inline, not toast) ──────────────────────────── */}
      {conflictWarning && (
        <div
          className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm px-4 py-3 rounded-lg"
          role="alert"
        >
          This job was modified elsewhere. Refresh to see the latest version.
          <button
            type="button"
            onClick={() => void fetchJob()}
            className="ml-3 text-xs underline hover:no-underline"
          >
            Refresh now
          </button>
        </div>
      )}

      {/* ── Tab bar ───────────────────────────────────────────────────────── */}
      <div
        className="flex gap-0 border-b border-white/10"
        role="tablist"
        aria-label="Job editor tabs"
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            aria-controls={`tabpanel-${tab.key}`}
            onClick={() => handleTabChange(tab.key)}
            className={`relative px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? "text-white border-brand-500"
                : "text-white/50 hover:text-white/80 border-transparent"
            }`}
          >
            {tab.label}
            {/* Unsaved-changes amber dot */}
            {dirty[tab.key] && (
              <span
                className="absolute -top-0.5 right-1 h-1.5 w-1.5 rounded-full bg-amber-400"
                aria-label="Unsaved changes"
              />
            )}
          </button>
        ))}
      </div>

      {/* ── Tab panels ────────────────────────────────────────────────────── */}
      <div className="min-h-48">
        {activeTab === "source" && (
          <div
            id="tabpanel-source"
            role="tabpanel"
            aria-labelledby="tab-source"
          >
            <SourceTablesPanel
              job={job}
              projectId={projectId}
              onSaved={handleSaved}
              onDirtyChange={handleSourceDirty}
              onConflict={handleConflict}
            />
          </div>
        )}

        {activeTab === "target" && (
          <div
            id="tabpanel-target"
            role="tabpanel"
            aria-labelledby="tab-target"
          >
            <TargetTablePanel
              job={job}
              projectId={projectId}
              onSaved={handleSaved}
              onDirtyChange={handleTargetDirty}
              onConflict={handleConflict}
            />
          </div>
        )}

        {activeTab === "columns" && (
          <div
            id="tabpanel-columns"
            role="tabpanel"
            aria-labelledby="tab-columns"
          >
            <ColumnMappingGrid
              job={job}
              projectId={projectId}
              onSaved={handleSaved}
              onDirtyChange={handleColumnsDirty}
              onConflict={handleConflict}
            />
          </div>
        )}

        {activeTab === "ctes" && (
          <div id="tabpanel-ctes" role="tabpanel" aria-labelledby="tab-ctes">
            <CTEStepPanel
              job={job}
              projectId={projectId}
              onSaved={handleSaved}
              onDirtyChange={handleCtesDirty}
              onConflict={handleConflict}
            />
          </div>
        )}

        {activeTab === "filters" && (
          <div
            id="tabpanel-filters"
            role="tabpanel"
            aria-labelledby="tab-filters"
          >
            <FilterPanel
              job={job}
              onSaved={handleSaved}
              onDirtyChange={handleFiltersDirty}
              onConflict={handleConflict}
            />
          </div>
        )}

        {activeTab === "dq" && (
          <div id="tabpanel-dq" role="tabpanel" aria-labelledby="tab-dq">
            <DQRulesPanel
              job={job}
              onSaved={handleSaved}
              onDirtyChange={handleDqDirty}
              onConflict={handleConflict}
            />
          </div>
        )}

        {activeTab === "strategy" && (
          <div
            id="tabpanel-strategy"
            role="tabpanel"
            aria-labelledby="tab-strategy"
          >
            <StrategyPanel
              job={job}
              pipelineId={pipelineId}
              jobId={jobId}
              onSaved={handleSaved}
              onDirtyChange={handleStrategyDirty}
              onConflict={handleConflict}
            />
          </div>
        )}

        {activeTab === "run" && (
          <div id="tabpanel-run" role="tabpanel" aria-labelledby="tab-run">
            <RunPanel
              pipelineId={pipelineId}
              jobId={jobId}
              job={job}
              onJobChanged={handleJobChanged}
            />
          </div>
        )}

        {activeTab === "sql-preview" && (
          <div
            id="tabpanel-sql-preview"
            role="tabpanel"
            aria-labelledby="tab-sql-preview"
          >
            <SQLPreviewPanel pipelineId={pipelineId} jobId={jobId} job={job} />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConnectionInfoBar — shows source and target DB connection details
// ---------------------------------------------------------------------------

/** Derive a short human-readable label from a connector config dict. */
function connectorLabel(type: string, config: Record<string, string>): string {
  const t = type.toLowerCase();
  if (t === "databricks") {
    const catalog = config.catalog ?? "";
    const schema = config.schema ?? config.database ?? "";
    const host = (config.workspace_host ?? config.host ?? "").replace(
      /^https?:\/\//,
      "",
    );
    const shortHost = host.split(".")[0] ?? host;
    return [shortHost, catalog, schema].filter(Boolean).join(" / ");
  }
  if (t === "mysql" || t === "postgres" || t === "oracle" || t === "redshift") {
    const host = config.host ?? "";
    const port = config.port ? `:${config.port}` : "";
    const db = config.database ?? config.schema ?? "";
    return `${host}${port}${db ? `/${db}` : ""}`;
  }
  if (t === "snowflake") {
    const acct = config.account ?? config.host ?? "";
    const db = config.database ?? "";
    const schema = config.schema ?? "";
    return [acct, db, schema].filter(Boolean).join(" / ");
  }
  // Generic fallback: host or first config value
  return (
    config.host ?? config.workspace_host ?? Object.values(config)[0] ?? type
  );
}

function ConnectionInfoBar({ pipeline }: { pipeline: PEPipeline }) {
  const srcLabel = connectorLabel(pipeline.sourceType, pipeline.sourceConfig);
  const tgtLabel = connectorLabel(pipeline.targetType, pipeline.targetConfig);

  return (
    <div
      className="flex items-center gap-2 mt-1 pl-[3.25rem] flex-wrap"
      aria-label="Pipeline connection"
    >
      {/* Source */}
      <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-md px-2.5 py-1">
        <Database
          size={11}
          className="text-white/40 shrink-0"
          aria-hidden="true"
        />
        <span className="text-[10px] font-medium text-white/50 uppercase tracking-wide">
          {pipeline.sourceType}
        </span>
        <span className="text-[10px] text-white/30 mx-1" aria-hidden="true">
          ·
        </span>
        <span className="text-[10px] text-white/60 font-mono" title={srcLabel}>
          {srcLabel}
        </span>
      </div>

      {/* Arrow */}
      <ArrowRight
        size={12}
        className="text-white/20 shrink-0"
        aria-hidden="true"
      />

      {/* Target */}
      <div className="flex items-center gap-1.5 bg-brand-500/10 border border-brand-500/20 rounded-md px-2.5 py-1">
        <Database
          size={11}
          className="text-brand-400/60 shrink-0"
          aria-hidden="true"
        />
        <span className="text-[10px] font-medium text-brand-400/70 uppercase tracking-wide">
          {pipeline.targetType}
        </span>
        <span className="text-[10px] text-white/30 mx-1" aria-hidden="true">
          ·
        </span>
        <span
          className="text-[10px] text-brand-300/70 font-mono"
          title={tgtLabel}
        >
          {tgtLabel}
        </span>
      </div>
    </div>
  );
}
